import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

const LegalLayout = ({ title, updated, children, testId }) => (
  <div data-testid={testId}>
    <Navbar />
    <header className="bg-[#3c003a] relative grain pt-40 pb-16">
      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-10">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-white/60 hover:text-[#ffc4e4] text-sm mb-8 transition-colors"
          data-testid="legal-back-link"
        >
          <ArrowLeft size={16} /> Back to home
        </Link>
        <h1 className="font-serif text-white text-4xl sm:text-5xl tracking-[-0.02em] mb-3">{title}</h1>
        <p className="text-white/45 text-sm">Last updated: {updated}</p>
      </div>
    </header>
    <main className="bg-[#fafaf7] py-16">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 space-y-10">{children}</div>
    </main>
    <Footer />
  </div>
);

const Section = ({ title, children }) => (
  <section>
    <h2 className="font-serif text-[#91008d] text-2xl mb-3">{title}</h2>
    <div className="text-[#121212]/70 text-[15px] leading-[1.8] space-y-3">{children}</div>
  </section>
);

export const TermsPage = () => (
  <LegalLayout title="Terms of Service" updated="August 2026" testId="terms-page">
    <Section title="1. Agreement">
      <p>
        By booking a photo booth rental with GlamBooth Cayman ("we", "us"), you agree to these
        Terms of Service. A booking is confirmed once we accept your inquiry and confirm your
        event date in writing.
      </p>
    </Section>
    <Section title="2. Bookings & Payment">
      <p>
        Package prices are listed in USD and cover the rental duration selected. Add-ons are
        charged as listed at the time of booking. A deposit may be required to secure your date;
        the remaining balance is due before or on the event date unless otherwise agreed.
      </p>
    </Section>
    <Section title="3. Cancellations & Rescheduling">
      <p>
        Cancellations made 14 or more days before the event receive a full refund of any deposit.
        Cancellations within 14 days may forfeit the deposit. We will do our best to accommodate
        one free reschedule, subject to availability.
      </p>
    </Section>
    <Section title="4. Event Requirements">
      <p>
        The client must provide a safe, level setup area (approximately 10 x 10 ft), access to a
        standard power outlet within 25 ft, and reasonable shelter from weather for outdoor
        events. Setup and takedown time is included and does not count against your rental hours.
      </p>
    </Section>
    <Section title="5. Conduct & Damage">
      <p>
        A professional attendant supervises the booth at all times. The client is responsible for
        any damage to the equipment caused by guests through misuse. We reserve the right to pause
        service if the equipment or attendant's safety is at risk.
      </p>
    </Section>
    <Section title="6. Media & Sharing">
      <p>
        Guests receive their photos, GIFs, Boomerangs, and videos via instant digital sharing.
        With your permission, we may feature select event media in our gallery and social
        channels. Let us know at booking if you prefer your event media to remain private.
      </p>
    </Section>
    <Section title="7. Liability">
      <p>
        Our liability is limited to the amount paid for the rental. We are not liable for delays
        or failures caused by events beyond our reasonable control, including severe weather,
        power outages, or venue restrictions.
      </p>
    </Section>
    <Section title="8. Contact">
      <p>
        Questions about these terms? Contact us at info@glamboothcayman.com or +1 (345) 328-6172.
      </p>
    </Section>
  </LegalLayout>
);

export const PrivacyPage = () => (
  <LegalLayout title="Privacy Policy" updated="August 2026" testId="privacy-page">
    <Section title="1. What We Collect">
      <p>
        When you submit a booking inquiry we collect your name, email, event date, venue, event
        type, guest count, and package/add-on preferences. If you subscribe to our newsletter we
        collect your email address.
      </p>
    </Section>
    <Section title="2. How We Use It">
      <p>
        We use your information solely to respond to your inquiry, manage your booking, and — if
        you subscribed — send occasional updates and offers. We do not sell or rent your personal
        information to third parties.
      </p>
    </Section>
    <Section title="3. Event Photos & Media">
      <p>
        Media captured in the booth is delivered to you and your guests. We only feature event
        media publicly (gallery, social media) with the event host's permission.
      </p>
    </Section>
    <Section title="4. Storage & Security">
      <p>
        Inquiry and subscriber data is stored securely and access is restricted to the GlamBooth
        Cayman team. We retain booking records only as long as needed for business and legal
        purposes.
      </p>
    </Section>
    <Section title="5. Your Choices">
      <p>
        You may unsubscribe from the newsletter at any time, and you can request access to or
        deletion of your personal data by emailing info@glamboothcayman.com.
      </p>
    </Section>
    <Section title="6. Contact">
      <p>
        For any privacy questions, reach us at info@glamboothcayman.com or +1 (345) 328-6172,
        Grand Cayman, Cayman Islands.
      </p>
    </Section>
  </LegalLayout>
);
